"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { LogIn } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useChatbot } from "@/contexts/ChatbotContext";
import ChatbotInline from "@/components/chatbot/ChatbotInline";
import botsIcon from "@/assets/img3d/Avatar.png";
import { AUTH_BACKGROUND_IMAGE } from "@/constants/images";

// --- COMPOSANT TYPING EFFECT MODERNISÉ ---
// Curseur plus subtil et meilleure gestion des transitions
const TypingEffect = ({ onStageChange }) => {
  const words = ["Louer ?", "Acheter ?", "Vendre ?", "AWLO !"];
  const [wordIndex, setWordIndex] = React.useState(0);
  const [displayedText, setDisplayedText] = React.useState("");
  const [isDeleting, setIsDeleting] = React.useState(false);

  // Notify parent when wordIndex changes
  React.useEffect(() => {
    if (onStageChange) {
      // Utiliser requestAnimationFrame est plus fluide que setTimeout pour les mises à jour d'état liées à l'animation
      requestAnimationFrame(() => onStageChange(wordIndex));
    }
  }, [wordIndex, onStageChange]);

  React.useEffect(() => {
    let timer;
    const handleTyping = () => {
      const currentWord = words[wordIndex];
      if (isDeleting) {
        setDisplayedText(currentWord.substring(0, displayedText.length - 1));
      } else {
        setDisplayedText(currentWord.substring(0, displayedText.length + 1));
      }

      if (!isDeleting && displayedText === currentWord) {
        // Pause plus longue avant d'effacer
        timer = setTimeout(() => setIsDeleting(true), 2500);
      } else if (isDeleting && displayedText === "") {
        setIsDeleting(false);
        setWordIndex((prev) => (prev + 1) % words.length);
      }
    };

    // Vitesse de frappe plus naturelle, effacement plus rapide
    const typingSpeed = isDeleting ? 40 : 90;
    timer = setTimeout(handleTyping, typingSpeed);

    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, wordIndex, words]);

  return (
    <div className="relative inline-block">
      <span className="text-white tracking-tight">{displayedText}</span>
      {/* Curseur moderne : une fine ligne verticale qui clignote */}
      <span className="ml-1 inline-block h-[1em] w-[3px] bg-white animate-pulse align-middle mb-1"></span>
    </div>
  );
};

// --- MESSAGES DES BADGES ---
const getBadgeMessages = (stageIndex) => {
  const stages = [
    ["Des milliers de biens", "Recherche simplifiée", "Prix transparents"],
    ["Investissement sécurisé", "Accompagnement", "Financement facilité"],
    ["Estimation gratuite", "Mise en avant garantie", "Visites organisées"],
    ["Expertise reconnue", "Réseau professionnel", "Satisfaction garantie"]
  ];
  return stages[stageIndex] || stages[0];
};


// --- COMPOSANT HERO SECTION ---
export default function HeroSection({ className, style, onSearchSubmit }) {
  const headingId = React.useId();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { openChatbot } = useChatbot();
  const [currentStageIndex, setCurrentStageIndex] = React.useState(0);

  const badgeMessages = getBadgeMessages(currentStageIndex);

  return (
    <section
      role="region"
      aria-labelledby={headingId}
      className={cn("relative w-full h-screen min-h-[700px] overflow-hidden font-sans", className)}
      style={style}
    >
      {/* --- FOND --- */}
      {/* Image de fond : Utilisation d'une image plus sombre et de meilleure qualité */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105 animate-slow-zoom" // Léger zoom pour un effet de profondeur
        style={{
          backgroundImage: `url('${AUTH_BACKGROUND_IMAGE}')`,
        }}
      />
      
      {/* Voile sombre unifié : Plus élégant qu'un dégradé complexe, il assure la lisibilité du blanc */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-black/50" // Opacité ajustée pour un bon contraste
      />

      {/* --- CONTENU PRINCIPAL --- */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 h-full flex">
        {/* --- GAUCHE : TEXTE & EFFET TYPING - CENTRÉ VERTICALEMENT --- */}
        <div className="w-full lg:w-1/2 flex items-center justify-center lg:justify-start">
          <div className="text-center space-y-10 w-full">
            {/* Titre Principal */}
            <h1
              id={headingId}
              // Utilisation de polices système modernes pour une netteté maximale
              className={cn(
                "font-extrabold leading-[1.1] tracking-tight text-white",
                // Tailles de police responsives et plus équilibrées
                "text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl",
                // Ombre portée subtile pour détacher le texte
                "drop-shadow-lg"
              )}
            >
              <TypingEffect onStageChange={setCurrentStageIndex} />
            </h1>

            {/* Badges Simplifiés et Élégants */}
            <div className="flex flex-wrap items-center justify-center gap-3 w-full px-2 py-1">
              {badgeMessages.map((message, idx) => (
                <div
                  key={idx}
                  className={cn(
                    "inline-flex items-center",
                    // Padding plus fin pour un look plus léger
                    "px-4 py-2.5",
                    // Style "pilule" blanche translucide, sans ombre portée lourde
                    "rounded-full bg-white/20 backdrop-blur-sm",
                    "border border-white/30",
                    // Animation subtile au survol
                    "hover:bg-white/30 transition-all duration-300"
                  )}
                >
                  <span className="text-sm sm:text-base font-medium text-white whitespace-nowrap">
                    {message}
                  </span>
                </div>
              ))}
            </div>

            {/* Bouton Connexion (Si non connecté) - Déplacé sous les badges */}
            {!isAuthenticated && (
              <div className="flex justify-center w-full mt-4">
                <Button
                  onClick={() => navigate('/login')}
                  variant="ghost"
                  // Style "bouton fantôme" blanc avec bordure pour plus de visibilité
                  className={cn(
                    "text-white hover:text-white",
                    "bg-white/10 hover:bg-white/25",
                    "border-2 border-white/60 hover:border-white/90",
                    "backdrop-blur-sm",
                    "transition-all duration-300",
                    "px-6 py-2.5 rounded-full",
                    "font-medium text-base",
                    "flex items-center gap-2",
                    "shadow-lg hover:shadow-xl"
                  )}
                  aria-label="Se connecter"
                >
                  <span>Connexion</span>
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* --- DROITE : CHAT IA INLINE - EN HAUT --- */}
        <div className="hidden lg:flex lg:w-1/2 lg:items-start lg:justify-end lg:pt-16">
          <div className="w-full max-w-lg">
            
            <ChatbotInline />
          </div>
        </div>

        {/* CTA Mobile - Tablette et mobile */}
        <div className="lg:hidden w-full flex justify-center mt-8">
          <Button
            onClick={() => openChatbot('search')}
            className={cn(
              "w-full max-w-md h-14",
              "bg-gradient-to-r from-blue-600 to-blue-700",
              "hover:from-blue-700 hover:to-blue-800",
              "text-white font-bold text-lg rounded-2xl",
              "shadow-lg hover:shadow-xl transition-all duration-300",
              "flex items-center justify-center gap-3"
            )}
          >
            <img 
              src={botsIcon} 
              alt="Cynthia" 
              className="h-8 w-8 object-contain"
            />
            <span>Parler à Cynthia</span>
          </Button>
        </div>
      </div>
    </section>
  );
}